// ════════════════════════════════════════════════════════════════
// Lifestyle Assessment — Domain Interpretation Copy
// ════════════════════════════════════════════════════════════════
//
// Each domain has three load-level interpretations and 2–4 suggestions.
// Drop your wording into the strings below — no code changes required.
//
// 📋 FOR FEEDING CHATGPT
//   Tell ChatGPT: "Fill in the empty strings in this JSON for [domain name]
//   in the voice of a holistic wellness practitioner. Keep paragraphs to
//   3–4 sentences. Suggestions should be specific, kind, and actionable."
//
// 🌿 Three load levels:
//   • LOW  (0–25%   load) = Resilient   — affirm + maintain
//   • MED  (26–50%  load) = Compensating — gentle next steps
//   • HIGH (51–100% load) = Dysregulated — biggest leverage area
// ════════════════════════════════════════════════════════════════

export const domainCopy = {
  'food-quality': {
    low: {
      paragraph: "Your current patterns suggest a fairly resilient and supportive relationship with nourishment. The body appears to have access to many of the nutrients and building blocks it needs to repair, regulate, and sustain energy. This does not mean perfection, but rather a foundation that is helping your system stay adaptable under stress. Maintaining consistency, hydration, and food quality will help preserve this strong terrain over time.",
      suggestions: [
        "Continue prioritizing whole, minimally processed foods most of the time.",
        "Aim for consistent protein, fiber, minerals, and healthy fats throughout the day.",
        "Rotate foods seasonally to support microbial diversity and nutrient variety.",
        "Keep tuning into how foods make you feel, not just what labels claim.",
      ],
    },
    medium: {
      paragraph: "Your system may currently be compensating for nutritional gaps, inconsistent eating patterns, or inflammatory inputs. Often the body can 'push through' for quite some time before symptoms become more obvious, which is why small imbalances can quietly accumulate. This is not a sign of failure, only a sign that your body may be asking for steadier support and nourishment. Gentle improvements in food quality and rhythm can create surprisingly meaningful shifts in energy, digestion, mood, and recovery.",
      suggestions: [
        "Reduce ultra-processed foods and increase whole-food meals where possible.",
        "Focus on blood sugar balance by pairing carbohydrates with protein and healthy fats.",
        "Increase mineral-rich foods such as leafy greens, sea vegetables, pumpkin seeds, and quality salt.",
        "Notice whether stress, rushing, or emotional overwhelm are influencing eating habits.",
      ],
    },
    high: {
      paragraph: "Your results suggest that food quality and nutrient density may currently be placing a significant load on the system. When the body lacks consistent nourishment, it often shifts into survival-style compensation, which can influence inflammation, hormones, digestion, energy production, cravings, mood, and recovery capacity. This is not about restriction or perfectionism. It is about creating a more supportive internal environment so the body can move from constant compensation toward repair and regulation.",
      suggestions: [
        "Begin simplifying meals around real, whole foods with fewer additives and inflammatory oils.",
        "Increase hydration and mineral intake to support cellular energy and detoxification pathways.",
        "Reduce frequency of highly processed snacks, refined sugars, and artificial ingredients.",
        "Consider building meals around a consistent structure: protein, colorful plants, healthy fats, and fiber.",
      ],
    },
    relatedPrograms: [
      { id: 'feel-good-detox', title: 'Feel Good Detox', why: "Supports reduction of inflammatory food inputs while helping rebuild awareness around nourishment, energy, and digestion." },
      { id: 'crap-cleanse', title: 'C.R.A.P. Cleanse', why: "Helps simplify food choices and reduce common processed and inflammatory dietary stressors." },
      { id: 'terrain', title: 'Terrain / Slow Wellness', why: "Explores how nourishment, stress, environment, emotions, and lifestyle collectively shape the body's internal environment. Encourages sustainable, realistic lifestyle shifts." },
    ],
  },
  'toxic-exposure': {
    low: { paragraph: '', suggestions: [] },
    medium: { paragraph: '', suggestions: [] },
    high: { paragraph: '', suggestions: [] },
    relatedPrograms: [],
  },
  'sleep-recovery': {
    low: { paragraph: '', suggestions: [] },
    medium: { paragraph: '', suggestions: [] },
    high: { paragraph: '', suggestions: [] },
    relatedPrograms: [],
  },
  'movement-posture': {
    low: { paragraph: '', suggestions: [] },
    medium: { paragraph: '', suggestions: [] },
    high: { paragraph: '', suggestions: [] },
    relatedPrograms: [],
  },
  'stress-mindset': {
    low: { paragraph: '', suggestions: [] },
    medium: { paragraph: '', suggestions: [] },
    high: { paragraph: '', suggestions: [] },
    relatedPrograms: [],
  },
  'breathing-airway': {
    low: { paragraph: '', suggestions: [] },
    medium: { paragraph: '', suggestions: [] },
    high: { paragraph: '', suggestions: [] },
    relatedPrograms: [],
  },
  'environment-rhythm': {
    low: { paragraph: '', suggestions: [] },
    medium: { paragraph: '', suggestions: [] },
    high: { paragraph: '', suggestions: [] },
    relatedPrograms: [],
  },
  'nervous-system': {
    low: { paragraph: '', suggestions: [] },
    medium: { paragraph: '', suggestions: [] },
    high: { paragraph: '', suggestions: [] },
    relatedPrograms: [],
  },
};

// Helper — fetch the right load level for a given percentage
export const getCopyForScore = (domainId, percentage) => {
  const entry = domainCopy[domainId];
  if (!entry) return null;
  if (percentage <= 25) return { level: 'low', ...entry.low, relatedPrograms: entry.relatedPrograms };
  if (percentage <= 50) return { level: 'medium', ...entry.medium, relatedPrograms: entry.relatedPrograms };
  return { level: 'high', ...entry.high, relatedPrograms: entry.relatedPrograms };
};
